import React, { useEffect, useMemo, useState } from "react";
import { ExternalLink, Loader2, Mail, Save, Send, ShieldCheck } from "lucide-react";
import MainLayout from "../components/CommonComponents/MainLayout";
import { fetchMailConfig, sendTestMailConfig, updateMailConfig } from "../services/mailConfigApi";
import { showErrorToast, showSuccessToast } from "../utils/toast";

const initialForm = {
  tenant_id: "",
  client_id: "",
  client_secret: "",
  sender_email: "info@zerooneitinc.com",
  recipient_email: "info@zerooneitinc.com",
  client_secret_expires_at: "",
};

const formatExpiration = (value) => {
  if (!value) return "";
  return new Date(`${value}T00:00:00`).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const getSecretStatus = ({ hasSecret, expirationDate }) => {
  if (!hasSecret) {
    return {
      tone: "warning",
      message: "No client secret is saved yet.",
    };
  }

  if (!expirationDate) {
    return {
      tone: "success",
      message: "Client secret is saved.",
    };
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const expiresAt = new Date(`${expirationDate}T00:00:00`);
  const daysRemaining = Math.ceil((expiresAt.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  const formattedDate = formatExpiration(expirationDate);

  if (daysRemaining < 0) {
    return {
      tone: "danger",
      message: `Client secret expired on ${formattedDate}.`,
    };
  }

  if (daysRemaining <= 30) {
    return {
      tone: "warning",
      message: `Client secret expires in ${daysRemaining} day${daysRemaining === 1 ? "" : "s"} on ${formattedDate}.`,
    };
  }

  return {
    tone: "success",
    message: `Client secret is valid until ${formattedDate}.`,
  };
};

const bannerToneClasses = {
  success: "border-emerald-300 bg-emerald-50 text-emerald-900",
  warning: "border-amber-300 bg-amber-50 text-amber-900",
  danger: "border-rose-300 bg-rose-50 text-rose-900",
};

export default function MailConfigPage() {
  const [form, setForm] = useState(initialForm);
  const [hasSecret, setHasSecret] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const secretStatus = useMemo(
    () => getSecretStatus({ hasSecret, expirationDate: form.client_secret_expires_at }),
    [form.client_secret_expires_at, hasSecret]
  );

  const loadConfig = async () => {
    setLoading(true);
    try {
      const response = await fetchMailConfig();
      const config = response.data || {};
      setForm({
        tenant_id: config.tenant_id || "",
        client_id: config.client_id || "",
        client_secret: "",
        sender_email: config.sender_email || initialForm.sender_email,
        recipient_email: config.recipient_email || initialForm.recipient_email,
        client_secret_expires_at: config.client_secret_expires_at || "",
      });
      setHasSecret(Boolean(config.has_client_secret));
    } catch (error) {
      console.error("Failed to load mail configuration:", error);
      showErrorToast("Failed to load mail configuration.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConfig();
  }, []);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const response = await updateMailConfig(form);
      const config = response.data?.config || {};
      setForm((prev) => ({
        ...prev,
        tenant_id: config.tenant_id || prev.tenant_id,
        client_id: config.client_id || prev.client_id,
        client_secret: "",
        sender_email: config.sender_email || prev.sender_email,
        recipient_email: config.recipient_email || prev.recipient_email,
        client_secret_expires_at: config.client_secret_expires_at || "",
      }));
      setHasSecret(Boolean(config.has_client_secret));
      showSuccessToast("Mail configuration saved.");
    } catch (error) {
      console.error("Failed to save mail configuration:", error);
      showErrorToast(error.response?.data?.message || "Failed to save mail configuration.");
    } finally {
      setSaving(false);
    }
  };

  const handleSendTestEmail = async () => {
    const recipientEmail = String(form.recipient_email || "").trim();
    if (!recipientEmail) {
      showErrorToast("Recipient Email is required before sending a test.");
      return;
    }

    setTesting(true);
    try {
      const response = await sendTestMailConfig({ recipient_email: recipientEmail });
      showSuccessToast(response.data?.message || `Test email sent to ${recipientEmail}.`);
    } catch (error) {
      console.error("Failed to send test email:", error);
      showErrorToast(error.response?.data?.message || "Failed to send test email.");
    } finally {
      setTesting(false);
    }
  };

  return (
    <MainLayout>
      <div className="mx-auto flex min-h-screen w-full max-w-6xl flex-col gap-7 px-4 py-8">
        <section>
          <div className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-500">
            <Mail className="h-4 w-4" />
            Microsoft Graph
          </div>
          <h1 className="mt-4 text-4xl font-bold tracking-tight text-slate-950">Mail Configuration</h1>
          <p className="mt-3 max-w-3xl text-sm leading-6 text-slate-600">
            Save the Microsoft credentials used by SaaS POS email notifications. The client secret value is write-only; leave it blank to keep the current saved secret.
          </p>
        </section>

        <div className={`flex items-center justify-between gap-3 rounded-lg border px-4 py-3 text-sm ${bannerToneClasses[secretStatus.tone]}`}>
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4" />
            <span>{secretStatus.message}</span>
          </div>
          <a
            href="https://entra.microsoft.com/#view/Microsoft_AAD_RegisteredApps/ApplicationMenuBlade/~/Credentials/appId/f8c08f17-5427-4722-a943-fa6ed8a912f1/isMSAApp~/false"
            target="_blank"
            rel="noreferrer"
            className="inline-flex shrink-0 items-center gap-1 font-semibold text-blue-700 underline-offset-2 hover:underline"
          >
            Open Microsoft Entra
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </div>

        <form onSubmit={handleSubmit} className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
          <fieldset disabled={loading || saving || testing} className="grid gap-5 disabled:cursor-wait disabled:opacity-75">
            <label className="block">
              <span className="mb-2 block text-sm font-semibold text-slate-900">Tenant ID</span>
              <input
                name="tenant_id"
                value={form.tenant_id}
                onChange={handleChange}
                className="w-full rounded-md border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-slate-400 focus:bg-white"
                required
              />
            </label>

            <label className="block">
              <span className="mb-2 block text-sm font-semibold text-slate-900">Client ID</span>
              <input
                name="client_id"
                value={form.client_id}
                onChange={handleChange}
                className="w-full rounded-md border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-slate-400 focus:bg-white"
                required
              />
            </label>

            <label className="block">
              <span className="mb-2 block text-sm font-semibold text-slate-900">
                Client Secret {hasSecret ? "(leave blank to keep saved secret)" : ""}
              </span>
              <input
                name="client_secret"
                type="password"
                value={form.client_secret}
                onChange={handleChange}
                className="w-full rounded-md border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-slate-400 focus:bg-white"
                autoComplete="new-password"
                required={!hasSecret}
              />
            </label>

            <div className="grid gap-5 md:grid-cols-2">
              <label className="block">
                <span className="mb-2 block text-sm font-semibold text-slate-900">Sender Email</span>
                <input
                  name="sender_email"
                  type="email"
                  value={form.sender_email}
                  onChange={handleChange}
                  className="w-full rounded-md border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-slate-400 focus:bg-white"
                  required
                />
              </label>

              <label className="block">
                <span className="mb-2 block text-sm font-semibold text-slate-900">Recipient Email</span>
                <input
                  name="recipient_email"
                  type="email"
                  value={form.recipient_email}
                  onChange={handleChange}
                  className="w-full rounded-md border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-slate-400 focus:bg-white"
                  required
                />
              </label>
            </div>

            <label className="block">
              <span className="mb-2 block text-sm font-semibold text-slate-900">Client Secret Expiration Date</span>
              <input
                name="client_secret_expires_at"
                type="date"
                value={form.client_secret_expires_at}
                onChange={handleChange}
                className="w-full rounded-md border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-slate-400 focus:bg-white"
              />
            </label>

            <div className="grid gap-3 md:grid-cols-[1fr,1fr]">
              <button
                type="button"
                onClick={handleSendTestEmail}
                disabled={testing || loading || saving || !hasSecret}
                className="inline-flex w-full items-center justify-center gap-2 rounded-md border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                title={!hasSecret ? "Save a client secret before sending a test email." : "Send a test email to the recipient address."}
              >
                {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                {testing ? "Sending Test Email..." : "Send Test Email"}
              </button>

              <button
                type="submit"
                disabled={saving}
                className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-slate-950 px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:cursor-wait disabled:opacity-70"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                {saving ? "Saving Mail Config..." : "Save Mail Config"}
              </button>
            </div>
          </fieldset>
        </form>
      </div>
    </MainLayout>
  );
}
